export class GeneralSettings {
  fileSizeLimitMB: number;
  showContactInquiryButton: boolean;
  showAddressInquiryButton: boolean;
  profileConfiguration: ProfileConfiguration[];
  settings: any;
}

export class ProfileConfiguration {
  name: string;
  type: string;
  accessRole: string;
  supplierTypes: string[];
  supplierCategories: number[];
  tierLevel: string;
}

export class SearchResult<T> {
  items: Array<T>;
  totalRecords: number;
}

// status of tool asset depends upon the statuses of Tesla assets added
export class ToolAsset {
  assetId: number;
  assetCode: string;
  assetDescription: string;
  assetStatus: string;
  cmtNumber: string;
  cmtStatus: string;
  createCMT: boolean;
  partNumbers: string;
  demandTypes: string
}

export class Asset {
  assetId: number;
  assetCode: string;
  assetDescription: string;
  assetStatus: string;
  partNumbers: string;

  // added new fields:
  demandTypes: string;
  residingLocation: string;
  residingLocationId: number;
  isAvailable: boolean;
  hasProductionDemand: boolean;
  hasFutureDemand: boolean;
  hasServiceDemand: boolean;
  hasNoDemand: boolean;
  showFutureStatus: boolean;
  showActiveStatus: boolean;
  showServiceStatus: boolean;
  showNoDemandStatus: boolean;
  cmtNumber: string;
  cmtStatus: string;
  createCMT: boolean;
  demandStatus: string;
}

export class AssetMismatch {
  constructor(
    public assetCode: string,
    public assetStatus: string,
    public partNumbers: string,
    public demandTypes: string,
    public cmtType: string
  ) { }
}

export class Attachment {
  id: number;
  name: string;
  type: number;
  key: string;
  storageUri: string;
}

export enum ToolLocationOrigin {
  none = 0,
  manufacturingLocation = 1,
  externalPartner = 2
}

export class ToolDeliverableType {
  id: number;
  toolSubcategoryId: number;
  deliverable: string;
  isGlobal: boolean;
  eventType: string;
  isDefault: boolean;
  status: string;
}

export class ToolDeliverable {
  eventType: number;
  deliverableTypeId: number;
  deliverable: string;
  startDate: Date;
  plannedDate: Date;
  actualDate: Date;
  approvedBy: string;
  isApproved: boolean;
  approver: string;
  approvalDate: Date;
  approvalComment: string;
  isDefault: boolean;
  attachments: Attachment[] = [];
  supplierComment: string;
  submitter: string;
  submitDate: Date;
  isCreatedByTeslaUser: boolean;
  status:number;
}

export enum ToolDeliverableStatus{
  Submitted = 1,
  Rejected = 2,
  Approved = 3,
  NotSubmitted = 4,
}

export class ToolParameter {
  typeId: number;
  typeName: string;
  value: string;
}

export class AssociatedPart {
  partId: number;
  partSupplierId: number;
  partNumber: string;
  partDescription: string;
  isActive: boolean;
  caseNumber: string;
  startDate: Date;
  status: number;
  processStepName: string;
  associatedRequirements: string[];
  mistakePreventions: string[];
  processSteps: ProcessStep[];
  id: number;
  createdOn: Date;
  demandType: string;
  isProcessFlowPart: boolean = false;
}

class ToolData {
  status: string;
  isTeslaAsset: boolean | null;
  isExpendable: boolean | null;
  isLease: boolean | null;
  leaseDate: Date | string;
  identifier: string;
  quantity: number;
  name: string;
  description: string;
  locationId: number;
  deliverable: ToolDeliverable[] = [];
  parameter: ToolParameter[] = [];
  tagAttachment: Attachment[] = [];
  assetAttachment: Attachment[] = [];
  assets: ToolAsset[] = [];
  associatedParts: AssociatedPart[] = [];
  numberOfParts: number;
  expirationDate: Date;
  statusMismatchOverrideComment: string;
}

export class Tool {
  id: number;
  supplierCode: string;
  isEquipment: boolean;
  toolSubcategoryId: number;
  toolSubcategory: string;
  toolCategoryId: number;
  toolCategory: string;
  assetId: number;
  data: ToolData = new ToolData();
  toolType: number;
}

export class DataThatIsBeingExported {
  srNumber: number;
  toolType: string;
  toolName: string;
  toolCategory: string;
  toolSubcategory: string;
  assetCode: string;
  assetDescription: string;
  assetStatus: string;
  toolStatus: string;
  expendable: string;
  expirationDate: string;
  partsUntilExpirations: number;
  associatedParts: string;
  demandStatus: string;
  event: string;
  deliverable: string;
  deliverableType: string;
  startDate: string;
  plannedDate: string;
  actualDate: string;
  deliverableStatus: String;
}

export class ToolCategory {
  id: number;
  category: string;
  isTool: boolean;
}

export class ToolSubcategory {
  id: number;
  toolCategoryId: number;
  subcategory: string;
  isGlobal: boolean;
}

export class ToolParameterType {
  id: number;
  toolSubcategoryId: number;
  type: string;
  supplierCode: string;
  isDefault: boolean;
  status: string;
}

export class AssetStatus {
  constructor(
    public name: string,
    public disabled: boolean
  ) { }
}


export class ToolFilter {
  isEquipment: boolean | null;
  toolId = 0;
  categoryId = 0;
  subcategoryId = 0;
  assetId = 0;
  status = "";
  name = "";
  location = "";

  props = "";
  take = 0;
  skip = 0;
  orderBy = "id";
  orderByDescending = false;
}

export class SupplierEvent {
  Name: string;
  StageId?: number;
  ReferenceIdentifier?: string;
  Properties: any;
  messages?: EventMessage[];
}
export class EventMessage {
  operation: string;
  status: string;
  message: string;
}

export class Entity {
  EntityID: number;
  Name: string;
  Description: string;
  Region: string;
}

export class User {
  adUsername: string;
  displayName: string;
  email: string;
  firstName: string;
  lastName: string;
  userID: number;
  buyer: boolean;
  managerUserID: number;
}

export class BaseModel {
  Id: number;
  Name: string;
  Description?: string;
}

export class BaseModelString {
  id: string;
  name: string;
  description?: string;
}

export class KeyValuePair<T1, T2> {
  Key: T1;
  Value: T2;
}

export class AddressLocation {
  id: number;
  name: string;
  address1: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
}

/* Tool Process Flow */

export interface ToolProcessFlow {
  partSupplierId: number;
  partNumber: string;
  partDescription: string;
  isActive: boolean;
  caseNumber: string;
  startDate: Date;
  status: number;
  processSteps: ProcessStep[];
  id: number;
  createdOn: Date;
  demandType: string;
}

export interface ProcessStep {
  processStepGuid: string;
  processStepNumber: string;
  processStepName: string;
  processStepType: number;
  processFlowId: number;
  description: string;
  diagramSchema: DiagramSchema;
  processStepDetails: ProcessStepDetails;
  id: number;
  createdBy: string;
  createdOn: Date;
  modifiedBy: string;
  modifiedOn: Date;
}

export interface DiagramSchema {
  stepGuid: string;
  position: Position;
  name: string;
  type: number;
  next: any[];
}

export interface Position {
  x: number;
  y: number;
}

export interface ProcessStepDetails {
  processStepElement: ProcessStepElement;
  processStepAttribute: ProcessStepAttribute;
  stability: Stability[];
}

export interface ProcessStepAttribute {
  cycleTimeMeasure: string;
  mtbfMeasure: string;
  mttrMeasure: string;
  sameAsParent: boolean;
  sameAsPrevious: boolean;
  plannedUnAllocatedTime: PlannedTime[];
  plannedDownTime: PlannedTime[];
  usePlannedCapacity: boolean;
  status: number;
}

export interface PlannedTime {
  name: string;
  frequency: string;
}

export interface ProcessStepElement {
  processNumber: string;
  processName: string;
  description: string;
  tools: any[];
  equipments: any[];
  requirements: Requirements;
  people: any[];
  mistakePrevention: MistakePrevention[];
  status: number;
}

export interface MistakePrevention {
  casualFactor: string;
  mechanism: string;
  validation: string;
  frequency: string;
  freq: number;
  description: string;
  file: string;
  status: number;
}

export interface Requirements {
  dependentRequirements: Requirement[];
  establishedRequirements: Requirement[];
  affectedRequirements: Requirement[];
}

export interface Requirement {
  guid: string;
  title: string;
}

export interface Stability {
  reqDescription: string;
  min: number;
  target: number;
  max: number;
  unitOfMeasure: UnitOfMeasure;
  comments: string;
  subGroupSize: number;
  subGroupFreq: string;
  subGroupFreqSize: number;
  personnel: string;
  status: number;
}

export interface UnitOfMeasure {
  unitID: number;
  unitOfMeasure: string;
  description: string;
  scale: number;
  enabled: boolean;
}

export class PartSupplierDetails {
  constructor(
    public partSupplierID: Number = 0,
    public partNumber: string = "",
    public partDescription: string = "",
    public partID: number = 0,
    public partPriority: string = "",
    public risk: string = "",
    public partSupplierTagID: string = "",
    public demandType: string = "",
    public partSummaryComment: string = "",
    public supplierCode: string = "",
    public supplierName: string = "",
    public scar: any = [],
    public deviation: any = [],
    public pcr: any = [],
    public pca: any = []
  ) { }
}

export interface Part {
  partID: number;
  partNumber: string;
  description: string;
  partTitle: string;
}

export class SupplierCommunicationValidation {
  userEmail: string;
  supplierCode: string;
  sentTo: string;
  reason: string;
  status: string;
  verifiedOn: Date;
  requestedBy: string;
  comment: string;
  isExpired: boolean;
  otp: number;
}
